import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import useResizeObserver from '@/components/_hooks/useResizeObserver';

interface Props {
  ref: RefObject<HTMLElement>;
  trackedDimensions?: 'width' | 'height' | 'both';
  /**
   * @description The timeout in milliseconds that specifies how long to wait after resizing ends before setting `isResizing` to `false`.
   */
  timeout?: number;
}

const useIsElementResizing = ({ ref, timeout = 300, trackedDimensions = 'both' }: Props): { isResizing: boolean } => {
  const [isResizing, setIsResizing] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isInitialCallbackCall = useRef(true);
  /**
   * `prevContentRectRef` is needed to track if trackedDimensions actually changed since the last resize event.
   */
  const prevContentRectRef = useRef<ResizeObserverEntry['contentRect']>();

  const handleResize = useCallback(({ contentRect }: ResizeObserverEntry) => {
    if (isInitialCallbackCall.current) {
      isInitialCallbackCall.current = false;
      prevContentRectRef.current = contentRect;
      // skip the initial call, because `useResizeObserver` calls onResize callback immediately after ResizeObserver created, before an element actually resized
      return;
    }

    if (!prevContentRectRef.current) {
      // This is just a type guard, it should never happen because we set `prevContentRectRef.current` in the initial `handleResize` call.
      return;
    }

    let trackedDimensionsChanged = false;

    if (trackedDimensions === 'width') {
      if (contentRect.width !== prevContentRectRef.current.width) {
        trackedDimensionsChanged = true;
      }
    } else if (trackedDimensions === 'height') {
      if (contentRect.height !== prevContentRectRef.current.height) {
        trackedDimensionsChanged = true;
      }
    } else {
      if (contentRect.width !== prevContentRectRef.current.width || contentRect.height !== prevContentRectRef.current.height) {
        trackedDimensionsChanged = true;
      }
    }

    prevContentRectRef.current = contentRect;

    if (!trackedDimensionsChanged) {
      return;
    }

    clearTimeout(timeoutRef.current);

    setIsResizing(true);

    timeoutRef.current = setTimeout(() => setIsResizing(false), timeout);
  }, []);

  useResizeObserver({
    ref,
    onResize: handleResize
  });

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  return { isResizing };
};

export default useIsElementResizing;
